
import { Options, prop, Vue } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import SelectSearchV2 from "@/app/ui/components/select-search/index.vue";
import DataWrapper from "@/app/ui/components/data-wrapper/index.vue";
import { debounce } from "lodash";
import { OptionsClass } from "@/domain/entities/MainApp";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { CourierManagementController } from "@/app/ui/controllers/CourierManagementController";
import { ApiRequestCourierVehicleList } from "@/data/payload/api/CourierManagementApiRequest";
import DateRangePicker from "@/app/ui/components/date-range-picker/index.vue";
import { formatDateNumber } from "@/app/infrastructures/misc/Utils";
import { STATUS_PICKUP_SCHEDULE } from "../../../list-pickup-v2/constants";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  applyActivePickupFilterToListPickupFilter,
  parseSummaryFilterQuery,
  PRESET_ACTIVE_PICKUP
} from "../../../utils/summary-filter-query";

class Props {
  modelValue = prop({ type: Object, required: true });
  errorType = prop({ type: String, default: "", required: true });
  isV2 = prop({ type: Boolean, default: false });
  loading = prop({ type: Boolean, default: false });
}
@Options({
  components: {
    SelectSearchV2,
    Checkbox,
    DateRangePicker
  },
  emits: ["update:modelValue", "request"]
})
class ListPickupFilterBar extends Vue.with(Props) {
  isAdvancedFilterOpened = false;
  onOpenedAdvancedFilter(event: any) {
    this.isAdvancedFilterOpened = event;
  }

  mounted() {
    this.filterProductsDelivery("");
    this.fetchVehicles("");
    this.hydrateFromQuery();
  }

  onSearch(value: string) {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      lsShipmentId: value
    });
    this.onChangeAdvancedFilter();
  }

  get searchValue() {
    return this.modelValue.lsShipmentId;
  }

  clearSearch() {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      lsShipmentId: ""
    });
    this.onChangeAdvancedFilter();
  }

  onChangeAdvancedFilter = debounce(() => {
    this.$emit("filter");
  }, 500);

  async onResetAdvancedFilter() {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      lsPickupSchedule: "",
      lsTransportTypeId: "",
      lsServiceCodes: "",
      lsLastStatus: ""
    });
    this.onResetQuery();
    this.$emit("reset");
  }

  get sumFilter(): number {
    return (
      (this.modelValue.lsTransportTypeId ? 1 : 0) +
      (this.modelValue.lsServiceCodes?.length ? 1 : 0) +
      (this.modelValue.lsLastStatus?.length ? 1 : 0)
    );
  }

  //product
  get productOptions() {
    const listData = BookingController.productsDelivery.data.map((e: any) => ({
      id: e.id,
      name: e.name,
      code: e.name,
      status: e.status
    }));

    const idx = listData.findIndex(
      (res) => res.name.toUpperCase() === "JUMBOPACKH2H"
    );
    listData.splice(idx, 1);

    return listData;
  }

  filterProductsDelivery = debounce((search: string) => {
    BookingController.getProductsDelivery({
      search,
      page: 1,
      limit: 99,
      status: "active"
    });
  }, 250);

  onChangeProduct() {
    this.onChangeAdvancedFilter();
  }

  fetchVehicles(search = "") {
    CourierManagementController.getListVehicle(
      new ApiRequestCourierVehicleList({ search })
    ).then((res) => {
      this.transportTypeOptions = res.data.map(
        (item: { id: number; vehicle_name: string }) => {
          return new OptionsClass({
            name: item.vehicle_name,
            value: String(item.vehicle_name)
          });
        }
      );
    });
  }

  // vehicle
  transportTypeOptions = [new OptionsClass()];

  onSelectVehicle(data: OptionsClass) {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      lsTransportTypeId: data.value
    });
    this.onChangeAdvancedFilter();
  }

  // status pickup schedule
  get isMalaysia(): boolean {
    return AccountController.accountData.countryCode?.toUpperCase() === "MY";
  }

  getStatusPickupScheduleName(item: OptionsClass): string {
    if (item.value === "SHPCRT") {
      return this.isMalaysia ? "Shipment Created" : "Pengiriman Dibuat";
    }
    if (item.value === "CRRSRC") {
      return this.isMalaysia ? "Search for Courier" : "Menunggu Kurir";
    }
    return this.$t(item.name) as string;
  }

  get statusPickupSchedule(): OptionsClass[] {
    return STATUS_PICKUP_SCHEDULE.map(
      (item: OptionsClass) =>
        new OptionsClass({
          ...item,
          name: this.getStatusPickupScheduleName(item)
        })
    );
  }

  selectStatus(data: OptionsClass) {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      lsLastStatus: data.value
    });
    this.onChangeAdvancedFilter();
  }

  onChangePickupSchedule(event: any) {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      lsPickupSchedule: event.periodeStart
        ? new Date(event.periodeStart).toLocaleDateString("fr-CA")
        : ""
    });
    this.onChangeAdvancedFilter();
  }

  //sort by
  sortByTitle = "";
  sortBySelect = false;
  sortByData = [
    { name: "Urutkan", value: "" },
    { name: "Jadwal Pick Up terdekat", value: "nearest" },
    { name: "Jadwal Pick Up terjauh", value: "farthest" },
    { name: "Jumlah tonase terbanyak", value: "max_pickup" },
    { name: "Jumlah tonase terkecil", value: "min_pickup" },
    { name: "Jumlah koli terbanyak", value: "max_koli" },
    { name: "Jumlah koli terkecil", value: "min_koli" }
  ];

  onOpenSortBy() {
    this.sortBySelect = true;
  }

  onCloseSortBy() {
    this.sortBySelect = false;
  }

  onSelectSortBy(name: string, value: string) {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      sortBy: value || ""
    });
    this.onChangeAdvancedFilter();
    this.sortByTitle = name;
  }

  setPeriode(value: string[]) {
    const startDate = value?.[0] ? formatDateNumber(value[0]) : "";
    const endDate = value?.[1] ? formatDateNumber(value[1]) : "";

    if (
      this.modelValue.startDate === startDate &&
      this.modelValue.endDate === endDate
    ) {
      return;
    }

    this.$emit("update:modelValue", {
      ...this.modelValue,
      startDate,
      endDate
    });

    this.onChangeAdvancedFilter();
  }

  async hydrateFromQuery() {
    this.$emit("update:modelValue", {
      lsShipmentId: "",
      lsPickupSchedule: "",
      lsTransportTypeId: "",
      lsServiceCodes: "",
      lsLastStatus: "",
      sortBy: "",
      startDate: "",
      endDate: ""
    });

    const preset = parseSummaryFilterQuery(this.$route.query);

    if (preset !== PRESET_ACTIVE_PICKUP) {
      this.onChangeAdvancedFilter();
      return;
    }

    const filter = applyActivePickupFilterToListPickupFilter(this.modelValue);
    this.$emit("update:modelValue", filter);
    this.onChangeAdvancedFilter();
  }

  onResetQuery() {
    const query = {
      ...this.$route.query
    };

    delete query.summary_filter;

    this.$router.replace({
      query
    });
  }
}

export default ListPickupFilterBar;
